import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    this.#showCarouselArrows();

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Moves the existing carousel directly to the selected variant image.
   * The gallery is not rebuilt, preventing the black image from flashing.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const updatedMediaGallery =
          html.querySelector('media-gallery');

        if (!updatedMediaGallery) return;

        const selectedMediaIndex = Number.parseInt(
          updatedMediaGallery.dataset.selectedMediaIndex ?? '0',
          10
        );

        if (Number.isNaN(selectedMediaIndex)) return;

        this.slideshow?.select(
          selectedMediaIndex,
          undefined,
          { animate: false }
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Keeps the desktop carousel chevrons visible.
   */
  #showCarouselArrows = () => {
    requestAnimationFrame(() => {
      if (this.presentation !== 'carousel') return;

      const arrows = this.querySelectorAll(
        'slideshow-arrows .slideshow-control'
      );

      for (const arrow of arrows) {
        if (!(arrow instanceof HTMLElement)) continue;

        arrow.style.opacity = '1';
        arrow.style.paddingInline = '0 var(--padding-md)';
      }
    });
  };

  /**
   * Handles a selection from the zoom gallery.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * Opens the zoom gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads a high-resolution image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}